import React, { useState } from "react";
import { Button, Popconfirm, Tag, Tooltip, message } from "antd";
import { CrownOutlined, EditOutlined, PercentageOutlined, StopOutlined } from "@ant-design/icons";

import * as api from "../controller";
import { OFFER_STATUS, claimByText, describeOffer, describeOfferPlans } from "./offerMath";
import { shortDate } from "./planState";
import "./billing.css";

/**
 * The special offers on an office or a practice, newest first; closed ones
 * (expired, revoked) sink to the bottom, dimmed. An office also sees the offers
 * it inherits from its practice — revoking one of those revokes it for every
 * office, and the confirm says so.
 *
 * `officeNames`: {office_id: name}, to say who claimed a practice-wide offer.
 */
export default function OfferList({ offers, officeNames, practiceName, onEdit, onChanged, emptyText }) {
  const [busy, setBusy] = useState(null);

  const revoke = async (offer) => {
    setBusy(offer.id);
    const r = await api.updateOffer(offer.id, { revoke: true });
    setBusy(null);
    if (r.status >= 200 && r.status < 300) {
      message.success("Offer revoked. Anyone who already claimed it keeps their price.");
      if (onChanged) onChanged();
    } else {
      message.error((r.json && r.json.detail) || "Couldn't revoke the offer.");
    }
  };

  const rank = { open: 0, expired: 1, revoked: 2 };
  const rows = [...(offers || [])].sort(
    (a, b) => (rank[a.status] - rank[b.status]) || String(b.created_at).localeCompare(String(a.created_at)),
  );
  if (!rows.length) return <div className="crm-empty">{emptyText || "No special offers."}</div>;

  return (
    <div className="ub-offers">
      {rows.map((o) => {
        const status = OFFER_STATUS[o.status] || OFFER_STATUS.open;
        const dim = o.status !== "open";
        const claims = o.claims || [];
        const claimText = claims.length
          ? claims.length === 1
            ? `Claimed${officeNames && officeNames[claims[0].office_id] ? ` by ${officeNames[claims[0].office_id]}` : ""} — ${claims[0].tier_title} (${shortDate(claims[0].claimed_at)})`
            : `Claimed by ${claims.length} offices`
          : "Not claimed yet";
        return (
          <div key={o.id} className={`ub-offer${dim ? " is-dim" : ""}`}>
            <div className="ub-offer-icon">{o.kind === "plan" ? <CrownOutlined /> : <PercentageOutlined />}</div>
            <div className="ub-offer-main">
              <div className="ub-offer-title">{describeOffer(o)}</div>
              {o.kind === "plan" ? (
                <div className="ub-offer-plans">
                  {(o.tiers || []).map((t) => t.title).join(" · ")}
                </div>
              ) : (
                <div className="ub-offer-plans">On {describeOfferPlans(o)}</div>
              )}
              <div className="ub-offer-meta">
                {o.label ? <span className="ub-offer-label">&ldquo;{o.label}&rdquo;</span> : null}
                <span>{o.status === "revoked" ? `Revoked ${shortDate(o.revoked_at)}` : claimByText(o.expires_on)}</span>
                <span>{claimText}</span>
                <span>Added {shortDate(o.created_at)}{o.created_by ? ` by ${o.created_by}` : ""}</span>
              </div>
            </div>
            <div className="ub-offer-side">
              <div style={{ display: "flex", gap: 6 }}>
                {o.inherited ? (
                  <Tooltip zIndex={10050} title={`Put on the whole practice${practiceName ? ` (${practiceName})` : ""}`}>
                    <Tag>Practice-wide</Tag>
                  </Tooltip>
                ) : null}
                <Tag color={status.color}>{status.label}</Tag>
              </div>
              {o.status !== "revoked" ? (
                <div className="ub-offer-actions">
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => onEdit && onEdit(o)}>
                    Edit
                  </Button>
                  {o.status === "open" ? (
                    <Popconfirm
                      title="Revoke this offer?"
                      description={
                        o.inherited || o.scope === "practice"
                          ? "It's on the whole practice, so no office can claim it any more. Anyone who already did keeps their price."
                          : "No one can claim it any more. Anyone who already did keeps their price."
                      }
                      okText="Revoke"
                      okButtonProps={{ danger: true }}
                      zIndex={10050}
                      onConfirm={() => revoke(o)}
                    >
                      <Button size="small" type="text" danger icon={<StopOutlined />} loading={busy === o.id}>
                        Revoke
                      </Button>
                    </Popconfirm>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
